// Inline SVG icons for the Shapes sidebar. Each icon is a tiny stylised
// glyph in 24x24 viewBox with stroke-only paths so it reads at any size
// against the dark sidebar background. The map is keyed by the `icon`
// value used in `shapes.yaml`; unknown icons fall back to a plain dot.

import type { JSX } from "react";

type IconRenderer = () => JSX.Element;

const torch: IconRenderer = () => (
  <>
    <path d="M12 2c-1.5 2-3 4-3 6 0 2 1.5 3 3 3s3-1 3-3c0-2-1.5-4-3-6z" />
    <path d="M10 13h4l-1 8h-2z" />
  </>
);

const candle: IconRenderer = () => (
  <>
    <path d="M12 2c-1 1.5-2 3-2 4.5 0 1.4 1 2.5 2 2.5s2-1.1 2-2.5c0-1.5-1-3-2-4.5z" />
    <rect x="9" y="10" width="6" height="12" rx="1" />
  </>
);

const bonfire: IconRenderer = () => (
  <>
    <path d="M12 2c-2 3-4 5-4 8 0 2 2 4 4 4s4-2 4-4c0-3-2-5-4-8z" />
    <path d="M3 18h18" />
    <path d="M6 22l4-4" />
    <path d="M18 22l-4-4" />
    <path d="M9 22l3-4 3 4" />
  </>
);

const volcano: IconRenderer = () => (
  <>
    <path d="M2 22l8-14h4l8 14z" />
    <path d="M10 8c1-2 0-4 0-6" />
    <path d="M12 4c0-1 0-2 1-2" />
    <path d="M11 14h2" />
  </>
);

const mountain: IconRenderer = () => (
  <>
    <path d="M2 22l7-12 4 6 3-4 6 10z" />
    <path d="M9 10l1.5 2.5L13 9" />
  </>
);

const pond: IconRenderer = () => (
  <>
    <ellipse cx="12" cy="14" rx="9" ry="5" />
    <path d="M5 14c2-1 4-1 6 0" />
    <path d="M13 14c2-1 4-1 6 0" />
  </>
);

const snowman: IconRenderer = () => (
  <>
    <circle cx="12" cy="6" r="3" />
    <circle cx="12" cy="13" r="4" />
    <circle cx="12" cy="20" r="3" />
    <circle cx="11" cy="5.5" r="0.5" fill="currentColor" />
    <circle cx="13" cy="5.5" r="0.5" fill="currentColor" />
  </>
);

const tree: IconRenderer = () => (
  <>
    <circle cx="12" cy="8" r="6" />
    <path d="M12 14v8" />
  </>
);

const lamp: IconRenderer = () => (
  <>
    <circle cx="12" cy="6" r="4" />
    <path d="M9 10l3 12 3-12" />
    <path d="M5 6h2M17 6h2" />
    <path d="M6 2l1.5 1.5M16.5 3.5L18 2" />
  </>
);

const cannon: IconRenderer = () => (
  <>
    <rect x="3" y="10" width="14" height="6" rx="2" />
    <circle cx="20" cy="13" r="2" />
    <path d="M17 9l3-3" />
  </>
);

const cylinder: IconRenderer = () => (
  <>
    <rect x="9" y="3" width="6" height="18" rx="1" />
    <line x1="9" y1="8" x2="11" y2="8" />
    <line x1="9" y1="12" x2="11" y2="12" />
    <line x1="9" y1="16" x2="11" y2="16" />
  </>
);

const bowl: IconRenderer = () => (
  <>
    <path d="M3 11h18" />
    <path d="M4 11c0 5 4 8 8 8s8-3 8-8" />
  </>
);

const cup: IconRenderer = () => (
  <>
    <path d="M5 8h11v9c0 2-1 3-3 3H8c-2 0-3-1-3-3V8z" />
    <path d="M16 11h2c1 0 2 1 2 2s-1 2-2 2h-2" />
  </>
);

const microwave: IconRenderer = () => (
  <>
    <rect x="2" y="6" width="20" height="12" rx="1" />
    <rect x="4" y="8" width="11" height="8" />
    <line x1="17" y1="9" x2="17" y2="11" />
    <line x1="19" y1="9" x2="19" y2="11" />
    <line x1="17" y1="13" x2="20" y2="13" />
  </>
);

const oven: IconRenderer = () => (
  <>
    <rect x="3" y="3" width="18" height="18" rx="1" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <circle cx="7" cy="6" r="0.8" fill="currentColor" />
    <circle cx="11" cy="6" r="0.8" fill="currentColor" />
    <rect x="6" y="11" width="12" height="8" rx="0.5" />
    <line x1="9" y1="15" x2="15" y2="15" />
  </>
);

const fan: IconRenderer = () => (
  <>
    <circle cx="12" cy="12" r="2" />
    <path d="M12 10c-3-3-6-2-6 0s3 3 6 2z" />
    <path d="M12 14c3 3 6 2 6 0s-3-3-6-2z" />
    <path d="M10 12c-3-3-2-6 0-6s3 3 2 6z" />
    <path d="M14 12c3 3 2 6 0 6s-3-3-2-6z" />
  </>
);

const gun: IconRenderer = () => (
  <>
    <path d="M3 10h14v3H8l-2 5H4l-1-3v-5z" />
    <line x1="14" y1="10" x2="14" y2="13" />
  </>
);

const detonator: IconRenderer = () => (
  <>
    <rect x="4" y="10" width="16" height="9" rx="1" />
    <line x1="12" y1="10" x2="12" y2="5" />
    <rect x="9" y="3" width="6" height="3" rx="0.5" />
  </>
);

const timer: IconRenderer = () => (
  <>
    <circle cx="12" cy="13" r="7" />
    <line x1="12" y1="13" x2="12" y2="9" />
    <line x1="12" y1="13" x2="15" y2="14" />
    <rect x="10" y="2" width="4" height="2" rx="0.5" />
  </>
);

const bulb: IconRenderer = () => (
  <>
    <path d="M9 14c-2-1-3-3-3-5 0-3 3-6 6-6s6 3 6 6c0 2-1 4-3 5v2h-6v-2z" />
    <line x1="10" y1="18" x2="14" y2="18" />
    <line x1="10" y1="20" x2="14" y2="20" />
    <path d="M11 22h2" />
  </>
);

const beaker: IconRenderer = () => (
  <>
    <path d="M7 4h10" />
    <path d="M8 4v14a3 3 0 0 0 3 3h2a3 3 0 0 0 3-3V4" />
    <line x1="8" y1="9" x2="10" y2="9" />
    <line x1="8" y1="13" x2="10" y2="13" />
  </>
);

const flask: IconRenderer = () => (
  <>
    <path d="M10 3h4" />
    <path d="M10 3v6L4 19a1 1 0 0 0 1 1.5h14A1 1 0 0 0 20 19l-6-10V3" />
  </>
);

const round_flask: IconRenderer = () => (
  <>
    <path d="M10 3h4" />
    <path d="M10 3v5" />
    <path d="M14 3v5" />
    <circle cx="12" cy="14" r="6" />
  </>
);

const test_tube: IconRenderer = () => (
  <>
    <path d="M8 3h8" />
    <path d="M9 3v15a3 3 0 0 0 6 0V3" />
    <line x1="9" y1="11" x2="11" y2="11" />
    <line x1="9" y1="15" x2="11" y2="15" />
  </>
);

const funnel: IconRenderer = () => (
  <>
    <path d="M3 4h18l-7 9v8h-4v-8z" />
  </>
);

const pipette: IconRenderer = () => (
  <>
    <rect x="9" y="2" width="6" height="4" rx="1" />
    <path d="M11 6v15" />
    <path d="M13 6v15" />
  </>
);

const burner: IconRenderer = () => (
  <>
    {/* flame */}
    <path d="M12 2c-1.5 1.5-3 3.5-3 5.5 0 1.7 1.3 3 3 3s3-1.3 3-3c0-2-1.5-4-3-5.5z" />
    {/* barrel */}
    <rect x="10" y="11" width="4" height="6" />
    {/* base */}
    <path d="M5 19h14v3H5z" />
  </>
);

const retort_stand: IconRenderer = () => (
  <>
    {/* base */}
    <rect x="3" y="20" width="14" height="2" rx="0.5" />
    {/* rod */}
    <line x1="10" y1="2" x2="10" y2="20" />
    {/* clamp arm */}
    <path d="M10 11h6" />
    {/* ring */}
    <circle cx="18" cy="11" r="2.5" />
  </>
);

const watch_glass: IconRenderer = () => (
  <>
    <path d="M3 14c2-4 6-6 9-6s7 2 9 6" />
    <path d="M3 14h18" />
  </>
);

const mortar: IconRenderer = () => (
  <>
    {/* bowl */}
    <path d="M4 11h16" />
    <path d="M5 11c0 5 3 8 7 8s7-3 7-8" />
    {/* pestle */}
    <line x1="14" y1="9" x2="20" y2="3" />
    <circle cx="20.5" cy="3" r="1" />
  </>
);

const petri_dish: IconRenderer = () => (
  <>
    <ellipse cx="12" cy="14" rx="9" ry="3" />
    <path d="M3 14c0-1 4-2 9-2s9 1 9 2" />
  </>
);

const thermometer: IconRenderer = () => (
  <>
    <path d="M11 4a1 1 0 0 1 2 0v11" />
    <circle cx="12" cy="17" r="3" />
    <line x1="13.5" y1="6" x2="15" y2="6" />
    <line x1="13.5" y1="9" x2="15" y2="9" />
    <line x1="13.5" y1="12" x2="15" y2="12" />
  </>
);

const syringe: IconRenderer = () => (
  <>
    {/* plunger */}
    <line x1="3" y1="3" x2="3" y2="9" />
    <line x1="6" y1="6" x2="3" y2="9" />
    {/* barrel */}
    <rect x="6" y="9" width="8" height="6" />
    <line x1="9" y1="9" x2="9" y2="15" />
    <line x1="11" y1="9" x2="11" y2="15" />
    {/* needle */}
    <line x1="14" y1="12" x2="22" y2="12" />
  </>
);

const gas_cylinder: IconRenderer = () => (
  <>
    {/* valve */}
    <line x1="12" y1="2" x2="12" y2="5" />
    <rect x="10" y="5" width="4" height="2" />
    {/* body */}
    <rect x="6" y="7" width="12" height="14" rx="2" />
    <line x1="6" y1="11" x2="18" y2="11" />
  </>
);

const test_tube_rack: IconRenderer = () => (
  <>
    {/* base */}
    <rect x="2" y="17" width="20" height="4" rx="0.5" />
    {/* tubes */}
    <path d="M5 4v13" />
    <path d="M9 4v13" />
    <path d="M11 4v13" />
    <path d="M15 4v13" />
    <path d="M17 4v13" />
    <path d="M21 4v13" />
    <path d="M5 4h4" />
    <path d="M11 4h4" />
    <path d="M17 4h4" />
  </>
);

const ant: IconRenderer = () => (
  <>
    {/* three body segments */}
    <circle cx="6" cy="12" r="2" />
    <circle cx="12" cy="12" r="2.5" />
    <circle cx="18" cy="12" r="2" />
    {/* legs */}
    <path d="M11 11l-2-3M13 11l2-3M11 13l-2 3M13 13l2 3M10 12l-3 0M14 12l3 0" />
    {/* antennae */}
    <path d="M5 11l-2-3M5 13l-2 3" />
  </>
);

const fly: IconRenderer = () => (
  <>
    {/* wings */}
    <ellipse cx="8" cy="9" rx="4" ry="2" />
    <ellipse cx="16" cy="9" rx="4" ry="2" />
    {/* body */}
    <ellipse cx="12" cy="14" rx="2.5" ry="3.5" />
    {/* head */}
    <circle cx="12" cy="9" r="1.5" />
  </>
);

const flea: IconRenderer = () => (
  <>
    {/* compact body */}
    <ellipse cx="12" cy="13" rx="3.5" ry="3" />
    {/* hind leg arc (jumping) */}
    <path d="M14 15c2 1 3 3 4 5" />
    <path d="M14 11c2-1 3-3 4-5" />
    {/* antennae */}
    <path d="M10 11l-3-3" />
    <path d="M10 13l-3 3" />
  </>
);

const switchIcon: IconRenderer = () => (
  <>
    <rect x="3" y="9" width="18" height="6" rx="3" />
    <circle cx="16" cy="12" r="2" fill="currentColor" />
  </>
);

const signal_lamp: IconRenderer = () => (
  <>
    <circle cx="12" cy="9" r="5" />
    <line x1="12" y1="3" x2="12" y2="5" />
    <line x1="6" y1="9" x2="4" y2="9" />
    <line x1="20" y1="9" x2="18" y2="9" />
    <rect x="9" y="14" width="6" height="6" />
  </>
);

const wire: IconRenderer = () => (
  <>
    <path d="M3 12c3-3 6 3 9 0s6 3 9 0" />
  </>
);

const pulser: IconRenderer = () => (
  <>
    {/* central spark with rays */}
    <circle cx="12" cy="12" r="3" />
    <line x1="12" y1="3" x2="12" y2="6" />
    <line x1="12" y1="18" x2="12" y2="21" />
    <line x1="3" y1="12" x2="6" y2="12" />
    <line x1="18" y1="12" x2="21" y2="12" />
    <line x1="5" y1="5" x2="8" y2="8" />
    <line x1="19" y1="5" x2="16" y2="8" />
    <line x1="5" y1="19" x2="8" y2="16" />
    <line x1="19" y1="19" x2="16" y2="16" />
  </>
);

const pump: IconRenderer = () => (
  <>
    {/* base + body + handle + spout */}
    <rect x="6" y="14" width="8" height="6" />
    <line x1="10" y1="14" x2="10" y2="6" />
    <line x1="10" y1="6" x2="16" y2="6" />
    <path d="M14 17h6" />
  </>
);

const grapple: IconRenderer = () => (
  <>
    {/* rope */}
    <line x1="12" y1="2" x2="12" y2="12" />
    {/* central knot */}
    <circle cx="12" cy="13" r="1.5" fill="currentColor" />
    {/* claws fanning out */}
    <path d="M12 14l-5 6" />
    <path d="M12 14l5 6" />
    <path d="M12 14l-2 7" />
    <path d="M12 14l2 7" />
  </>
);

const rod: IconRenderer = () => (
  <>
    {/* angled cane */}
    <line x1="3" y1="20" x2="17" y2="6" />
    {/* line dropping */}
    <line x1="17" y1="6" x2="17" y2="18" />
    {/* hook */}
    <path d="M17 18c0 2 1.5 2.5 3 1.5" />
  </>
);

// Experiment icons: schematics rather than photographic glyphs,
// so the user reads the wiring diagram at a glance from the
// sidebar entry.
const lamp_circuit: IconRenderer = () => (
  <>
    <rect x="2" y="9" width="5" height="6" rx="1" />
    <line x1="7" y1="12" x2="17" y2="12" />
    <circle cx="20" cy="12" r="3" />
  </>
);

const parallel_lamps: IconRenderer = () => (
  <>
    {/* switch */}
    <rect x="2" y="10" width="4" height="4" rx="1" />
    {/* horizontal bus */}
    <line x1="6" y1="12" x2="14" y2="12" />
    {/* branches up + down */}
    <line x1="10" y1="12" x2="10" y2="4" />
    <line x1="10" y1="12" x2="10" y2="20" />
    {/* two lamps */}
    <circle cx="10" cy="3" r="2.5" />
    <circle cx="10" cy="21" r="2.5" />
    <circle cx="17" cy="12" r="2.5" />
  </>
);

const two_switch_or: IconRenderer = () => (
  <>
    {/* two switches */}
    <rect x="2" y="5" width="4" height="3" rx="0.5" />
    <rect x="2" y="16" width="4" height="3" rx="0.5" />
    {/* converging wires */}
    <line x1="6" y1="6.5" x2="14" y2="12" />
    <line x1="6" y1="17.5" x2="14" y2="12" />
    <line x1="14" y1="12" x2="20" y2="12" />
    {/* lamp */}
    <circle cx="22" cy="12" r="2" />
  </>
);

const powered_fan: IconRenderer = () => (
  <>
    {/* switch */}
    <rect x="2" y="14" width="4" height="4" rx="1" />
    {/* wire */}
    <line x1="6" y1="16" x2="13" y2="16" />
    {/* fan blades */}
    <circle cx="17" cy="12" r="1.5" fill="currentColor" />
    <path d="M17 10c-2-2-4-1-4 1s2 2 4 1z" />
    <path d="M17 14c2 2 4 1 4-1s-2-2-4-1z" />
    <path d="M15 12c-2-2-1-4 1-4s2 2 1 4z" />
    <path d="M19 12c2 2 1 4-1 4s-2-2-1-4z" />
  </>
);

const fountain: IconRenderer = () => (
  <>
    {/* basin */}
    <path d="M5 18h14v3a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1z" />
    {/* water column */}
    <path d="M12 18V6" />
    {/* spray arcs */}
    <path d="M12 6c-3 1-4 3-4 6" />
    <path d="M12 6c3 1 4 3 4 6" />
    {/* drop */}
    <circle cx="12" cy="3" r="1.2" fill="currentColor" />
  </>
);

const torch_switch: IconRenderer = () => (
  <>
    {/* circuit on top */}
    <rect x="2" y="3" width="3" height="3" rx="0.5" />
    <line x1="5" y1="4.5" x2="14" y2="4.5" />
    <circle cx="16" cy="4.5" r="2" />
    {/* candle on the bottom */}
    <path d="M9 11c-1 1-2 2-2 3.5 0 1 1 1.5 2 1.5s2-0.5 2-1.5c0-1.5-1-2.5-2-3.5z" />
    <rect x="7" y="16" width="4" height="6" rx="0.5" />
  </>
);

const light_bulb: IconRenderer = () => (
  <>
    {/* bulb dome */}
    <path d="M9 2c-3 0-5 2-5 5 0 2 1.5 3.5 2.5 5 0.5 0.7 1 1.4 1 2h3c0-0.6 0.5-1.3 1-2 1-1.5 2.5-3 2.5-5 0-3-2-5-5-5z"
          transform="translate(3 0)" />
    {/* filament */}
    <path d="M11 9c0.5 1 1 2 1 3M13 9c-0.5 1-1 2-1 3" />
    {/* screw base */}
    <line x1="10" y1="16" x2="14" y2="16" />
    <line x1="10" y1="18" x2="14" y2="18" />
    <path d="M11 20l1 2 1-2" />
  </>
);

const dot: IconRenderer = () => <circle cx="12" cy="12" r="4" />;

const ICONS: Record<string, IconRenderer> = {
  torch,
  candle,
  bonfire,
  volcano,
  mountain,
  pond,
  snowman,
  tree,
  lamp,
  cannon,
  cylinder,
  bowl,
  cup,
  microwave,
  oven,
  fan,
  gun,
  detonator,
  timer,
  bulb,
  // Lab glassware + apparatus.
  beaker,
  flask,
  round_flask,
  test_tube,
  funnel,
  pipette,
  burner,
  retort_stand,
  watch_glass,
  mortar,
  petri_dish,
  thermometer,
  syringe,
  gas_cylinder,
  test_tube_rack,
  // Insects + signal apparatus + hand tools.
  ant,
  fly,
  flea,
  switch: switchIcon,
  signal_lamp,
  wire,
  pulser,
  pump,
  grapple,
  rod,
  // Experiments — pre-built circuits + plumbing scenes.
  lamp_circuit,
  parallel_lamps,
  two_switch_or,
  powered_fan,
  fountain,
  torch_switch,
  light_bulb,
};

export interface ShapeIconProps {
  name?: string;
  size?: number;
}

export function ShapeIcon({ name, size = 18 }: ShapeIconProps) {
  const renderer = (name && ICONS[name]) || dot;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {renderer()}
    </svg>
  );
}
